/* ROMANTICO - PDP soundtrack player (liquid glass panel).
   Minimal, dependency-free HTML5 Audio. Opt-in only: playback starts on click,
   never autoplays. Volume fades in over 800ms up to 0.4 and fades back out on
   pause or when the page goes away. Custom element is guarded so it can be
   included per-block.

   requestAnimationFrame is used in two places only, both bounded:
     - the volume fade, which stops as soon as it reaches the target
     - the specular highlight, one frame per pointermove, mouse only
   There is no continuous loop. */
(function () {
  if (window.customElements && customElements.get('rom-pdp-player')) return;

  var FADE_MS = 800;
  var TARGET_VOLUME = 0.4;
  var SPECULAR_MAX = 4;

  function clockTime(seconds) {
    if (!isFinite(seconds) || seconds < 0) seconds = 0;
    var total = Math.floor(seconds);
    var minutes = Math.floor(total / 60);
    var rest = total % 60;
    return minutes + ':' + (rest < 10 ? '0' + rest : rest);
  }

  function prefersReducedMotion() {
    return !!(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  class RomPdpPlayer extends HTMLElement {
    connectedCallback() {
      this.audio = this.querySelector('.rom-player__audio');
      this.button = this.querySelector('.rom-player__toggle');
      if (!this.audio || !this.button) return;

      this.panel = this.querySelector('.rom-player__panel');
      this.sheen = this.querySelector('.rom-player__sheen');
      this.bar = this.querySelector('.rom-player__bar');
      this.track = this.querySelector('.rom-player__progress');
      this.elapsed = this.querySelector('.rom-player__elapsed');
      this.total = this.querySelector('.rom-player__duration');
      this.live = this.querySelector('.rom-player__live');

      this.playLabel = this.dataset.labelPlay || 'Play';
      this.pauseLabel = this.dataset.labelPause || 'Pause';
      this.playingText = this.dataset.livePlaying || 'Playing';
      this.pausedText = this.dataset.livePaused || 'Paused';

      this.fadeFrame = 0;
      this.specularFrame = 0;
      this.pointerX = 0;
      this.pointerY = 0;
      this.audio.volume = 0;

      this._onClick = this.toggle.bind(this);
      this._onTime = this.updateProgress.bind(this);
      this._onMeta = this.updateDuration.bind(this);
      this._onEnd = this.reset.bind(this);

      this.button.addEventListener('click', this._onClick);
      this.audio.addEventListener('timeupdate', this._onTime);
      this.audio.addEventListener('loadedmetadata', this._onMeta);
      this.audio.addEventListener('durationchange', this._onMeta);
      this.audio.addEventListener('ended', this._onEnd);

      var player = this;
      this._onPageHide = function () {
        player.stop(true);
      };
      this._onVisibility = function () {
        if (document.hidden && player.audio && !player.audio.paused) player.stop(false);
      };
      window.addEventListener('pagehide', this._onPageHide);
      document.addEventListener('visibilitychange', this._onVisibility);

      this.bindSpecular();
    }

    disconnectedCallback() {
      if (this.button && this._onClick) this.button.removeEventListener('click', this._onClick);
      if (this.audio) {
        if (this._onTime) this.audio.removeEventListener('timeupdate', this._onTime);
        if (this._onMeta) {
          this.audio.removeEventListener('loadedmetadata', this._onMeta);
          this.audio.removeEventListener('durationchange', this._onMeta);
        }
        if (this._onEnd) this.audio.removeEventListener('ended', this._onEnd);
        this.audio.pause();
      }
      if (this._onPageHide) window.removeEventListener('pagehide', this._onPageHide);
      if (this._onVisibility) document.removeEventListener('visibilitychange', this._onVisibility);
      if (this.panel && this._onPointerMove) {
        this.panel.removeEventListener('pointermove', this._onPointerMove);
        this.panel.removeEventListener('pointerleave', this._onPointerLeave);
      }
      if (this.fadeFrame) cancelAnimationFrame(this.fadeFrame);
      if (this.specularFrame) cancelAnimationFrame(this.specularFrame);
      this.fadeFrame = 0;
      this.specularFrame = 0;
    }

    /* ---- playback ------------------------------------------------------- */

    toggle() {
      if (!this.audio) return;
      if (this.audio.paused) {
        this.start();
      } else {
        this.stop(false);
      }
    }

    start() {
      this.audio.volume = 0;
      var played = this.audio.play();
      if (played && played.catch) played.catch(function () {});
      this.setAttribute('playing', '');
      this.button.setAttribute('aria-pressed', 'true');
      this.button.setAttribute('aria-label', this.pauseLabel);
      this.announce(this.playingText);
      this.fadeVolume(TARGET_VOLUME, null);
    }

    stop(immediate) {
      this.removeAttribute('playing');
      this.button.setAttribute('aria-pressed', 'false');
      this.button.setAttribute('aria-label', this.playLabel);
      this.announce(this.pausedText);
      if (!this.audio) return;
      if (immediate) {
        if (this.fadeFrame) cancelAnimationFrame(this.fadeFrame);
        this.fadeFrame = 0;
        this.audio.pause();
        this.audio.volume = 0;
        return;
      }
      var audio = this.audio;
      this.fadeVolume(0, function () {
        audio.pause();
      });
    }

    /* rAF runs for the length of the fade and then stops. */
    fadeVolume(target, done) {
      if (!this.audio) return;
      if (this.fadeFrame) cancelAnimationFrame(this.fadeFrame);
      var player = this;
      var from = this.audio.volume;
      var started = 0;
      if (from === target) {
        this.fadeFrame = 0;
        if (done) done();
        return;
      }
      this.fadeFrame = requestAnimationFrame(function step(now) {
        if (!started) started = now;
        var ratio = Math.min(1, (now - started) / FADE_MS);
        var value = from + (target - from) * ratio;
        player.audio.volume = Math.max(0, Math.min(1, value));
        if (ratio < 1) {
          player.fadeFrame = requestAnimationFrame(step);
        } else {
          player.fadeFrame = 0;
          if (done) done();
        }
      });
    }

    updateProgress() {
      if (!this.audio) return;
      if (this.elapsed) this.elapsed.textContent = clockTime(this.audio.currentTime);
      var duration = this.audio.duration;
      if (!duration || !isFinite(duration)) return;
      var pct = (this.audio.currentTime / duration) * 100;
      if (pct < 0) pct = 0;
      if (pct > 100) pct = 100;
      if (this.bar) this.bar.style.width = pct.toFixed(2) + '%';
      if (this.track) this.track.setAttribute('aria-valuenow', String(Math.round(pct)));
    }

    updateDuration() {
      if (!this.audio || !this.total) return;
      var duration = this.audio.duration;
      if (!duration || !isFinite(duration)) return;
      this.total.textContent = clockTime(duration);
    }

    reset() {
      this.removeAttribute('playing');
      if (this.button) {
        this.button.setAttribute('aria-pressed', 'false');
        this.button.setAttribute('aria-label', this.playLabel);
      }
      if (this.bar) this.bar.style.width = '0';
      if (this.track) this.track.setAttribute('aria-valuenow', '0');
      if (this.elapsed) this.elapsed.textContent = clockTime(0);
      if (this.audio) {
        this.audio.currentTime = 0;
        this.audio.volume = 0;
      }
      this.announce(this.pausedText);
    }

    announce(message) {
      if (!this.live || this.live.textContent === message) return;
      this.live.textContent = message;
    }

    /* ---- specular highlight --------------------------------------------- */

    bindSpecular() {
      if (!this.sheen || !this.panel) return;
      if (prefersReducedMotion()) return;
      var player = this;

      this._onPointerMove = function (event) {
        /* Mouse only: on touch the highlight would jump under the finger. */
        if (event.pointerType && event.pointerType !== 'mouse') return;
        player.pointerX = event.clientX;
        player.pointerY = event.clientY;
        if (player.specularFrame) return;
        player.specularFrame = requestAnimationFrame(function () {
          player.specularFrame = 0;
          player.moveSpecular();
        });
      };
      this._onPointerLeave = function () {
        if (player.specularFrame) {
          cancelAnimationFrame(player.specularFrame);
          player.specularFrame = 0;
        }
        player.sheen.style.transform = '';
      };

      this.panel.addEventListener('pointermove', this._onPointerMove);
      this.panel.addEventListener('pointerleave', this._onPointerLeave);
    }

    moveSpecular() {
      if (!this.sheen || !this.panel) return;
      var rect = this.panel.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      var x = ((this.pointerX - rect.left) / rect.width - 0.5) * 2;
      var y = ((this.pointerY - rect.top) / rect.height - 0.5) * 2;
      x = Math.max(-1, Math.min(1, x));
      y = Math.max(-1, Math.min(1, y));
      this.sheen.style.transform =
        'translate3d(' +
        (x * SPECULAR_MAX).toFixed(2) +
        'px, ' +
        (y * SPECULAR_MAX).toFixed(2) +
        'px, 0)';
    }
  }

  customElements.define('rom-pdp-player', RomPdpPlayer);
})();
